import { isDefined } from "@omni/utils/helper.utils";
import {
    ChangeEvent,
    KeyboardEvent,
    MouseEvent,
    ReactNode,
    SubmitEvent,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef
} from "react";

import { SearchBoxContextProvider } from "@/components/Search/SearchBar/components/SearchBox/context/searchBox.context";
import { useSearchBarContext } from "@/components/Search/SearchBar/context/searchBar.context";
import { getLastAutocompleteItem } from "@/components/Search/SearchBar/helpers/accessibility.helpers";
import { isValidQuery } from "@/redux/modules/value/search/helpers/search.helpers";
import { KeyNames } from "@/utils/enums";
import { createKeyHandler } from "@/utils/keyboard";

import focusElementByRef from "../../helpers/focus.helper";

type Props = {
    children: ReactNode;
};

export const Base = ({ children }: Props) => {
    const {
        isOpen,
        autocomplete,
        autocompleteRef,
        autocompleteActions,
        autocompleteState,
        onOpen,
        onClose
    } = useSearchBarContext();

    const inputRef = useRef<HTMLInputElement>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const resetButtonRef = useRef<HTMLButtonElement>(null);
    const submitButtonRef = useRef<HTMLButtonElement>(null);
    const queryRef = useRef<string | null>(null);

    const formProps = useMemo(
        () =>
            autocomplete.getFormProps({
                inputElement: inputRef.current
            }),
        [autocomplete]
    );
    const inputProps = useMemo(
        () =>
            autocomplete.getInputProps({
                inputElement: inputRef.current
            }),
        [autocomplete]
    );

    const forceFocusOnInputRef = useCallback(() => {
        const timeout = focusElementByRef(inputRef);
        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, []);

    const handleOnInputKeyDown = createKeyHandler<KeyboardEvent>([
        {
            keys: [KeyNames.ENTER],
            handler: (event) => {
                const activeItemId = autocompleteState?.activeItemId;
                if (isDefined(activeItemId)) return;

                event.stopPropagation();

                if (!isValidQuery(inputRef.current?.value)) {
                    event.preventDefault();
                } else {
                    inputProps.onKeyDown(event);
                }
            }
        },
        {
            keys: [KeyNames.TAB],
            handler: (event) => {
                if (!event.shiftKey) return;

                const lastAutocompleteItem = getLastAutocompleteItem();

                if (!isDefined(lastAutocompleteItem)) return;

                event.preventDefault();
                lastAutocompleteItem.focus();
            }
        },
        {
            defaultExcept: [KeyNames.TAB],
            handler: (event) => inputProps.onKeyDown(event)
        }
    ]);

    const handleOnResetClick = (event: MouseEvent) => {
        event.preventDefault();

        autocompleteState?.query && autocomplete.setQuery("");
        autocompleteActions?.setQuery("");

        onOpen();
        forceFocusOnInputRef();
    };

    const handleOnCloseClick = (event: MouseEvent) => {
        event.stopPropagation();
        onClose();
    };

    const handleOnOpenClick = (_event: MouseEvent) => {
        onOpen();
    };

    const handleOnInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        queryRef.current = event.target.value;
        inputProps.onChange(event);
    };

    const handleOnInputClick = (event: MouseEvent) => {
        forceFocusOnInputRef();
        inputProps.onClick(event);
    };

    const handleOnFormSubmit = (event: SubmitEvent) => {
        if (!isValidQuery(autocompleteState?.query) || !event.isTrusted) {
            event.preventDefault();
        } else {
            formProps.onSubmit(event);
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (isOpen) {
            forceFocusOnInputRef();
        }
    }, [isOpen]);

    useImperativeHandle(autocompleteRef, () => ({
        get input() {
            return inputRef.current;
        },
        get form() {
            return formRef.current;
        },
        get query() {
            return queryRef.current;
        },
        set query(query: string | null) {
            queryRef.current = query;
        }
    }));

    return (
        <SearchBoxContextProvider
            isOpen={isOpen}
            inputRef={inputRef}
            formRef={formRef}
            submitButtonRef={submitButtonRef}
            resetButtonRef={resetButtonRef}
            inputProps={{
                ...inputProps,
                onKeyDown: handleOnInputKeyDown,
                onChange: handleOnInputChange,
                onClick: handleOnInputClick
            }}
            formProps={{
                ...formProps,
                onSubmit: handleOnFormSubmit
            }}
            onCloseClick={handleOnCloseClick}
            onResetClick={handleOnResetClick}
            onOpenClick={handleOnOpenClick}
        >
            {children}
        </SearchBoxContextProvider>
    );
};
